import { components as c } from "@/components";
import ShockwaveDetailsFormFields from "@/features/shockwave/components/ShockwaveDetailsFormFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import Stepper from "@/components/common/Stepper";
import { useReplaceShockwaveDetails } from "@/features/shockwave/api/useReplaceShockwaveDetails";
import {
	formatShockwaveDetailsDefaultValues,
	ShockwaveDetailSchemaT,
	ShockwaveDetailsZ,
} from "@/features/shockwave/components/ShockwaveDetailsForm";
import useExtractShockwave from "@/features/shockwave/hooks/useExtractShockwave";
import FormErrors from "@/components/form/FormErrors";
import {
	useEventStepper,
} from "@/features/templates/hooks/useEventNavigationStore";

interface RouteParams {
	shockwaveDetailId: string;
	templateId: string;
}

const ShockwaveDetailsFormFieldsPage = () => {
	const { diaryId, eventId, shockwaveId } = Route.useParams();
	const shockwave = useExtractShockwave(diaryId, shockwaveId);
	const navigate = useNavigate();

	const replaceShockwaveDetails = useReplaceShockwaveDetails({
		diaryId: diaryId,
	});

	const form = useForm<ShockwaveDetailSchemaT>({
		resolver: zodResolver(ShockwaveDetailsZ),
		defaultValues: formatShockwaveDetailsDefaultValues(
			shockwaveId,
			shockwave,
		),
	});

	const { steps } = useEventStepper("shockwave-details");

	const onSubmit: SubmitHandler<ShockwaveDetailSchemaT> = async (
		formData,
	) => {
		replaceShockwaveDetails.mutate({
			...formData,
			shockwave_id: shockwaveId,
		}, {
			onSuccess: () => {
				navigate({
					to: "/patients/$diaryId",
					params: { diaryId },
				});
			},
		});
	};

	return (
		<div className="space-y-8">
			<Stepper steps={steps} />
			<c.ui.Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="w-full form"
				>
					<ShockwaveDetailsFormFields
						shockwaveId={shockwaveId ?? ""}
					/>

					<FormErrors />

					{/* Navigation Buttons */}
					<div className="flex justify-between gap-4 mt-8">
						<c.ui.Button
							type="button"
							variant="outline"
							onClick={() =>
								navigate({
									to: "/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId",
									params: { diaryId, eventId, shockwaveId },
								})}
						>
							Previous
						</c.ui.Button>
						<c.fc.NextButton />
					</div>
				</form>
			</c.ui.Form>
		</div>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId/details",
)({
	component: ShockwaveDetailsFormFieldsPage,
	loader: ({ params }: { params: RouteParams }) => {
		return {
			shockwaveDetailId: params.shockwaveDetailId,
		};
	},
});
